import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface FociSetting {
  enabled?: boolean;
  /** `true` (el booleano, nada más): TODAS las empresas, también las futuras (05-10-2026). */
  all?: boolean;
  /** SOLO esas empresas. Vacía o ausente: ninguna (auditoría 23-09 clientes#1). */
  companies?: string[];
}

/**
 * La MISMA regla que `public._foci_enabled()` en la base de datos: si cambia una, cambia la otra
 * (y las de los demás pilotos: src/__tests__/interruptoresTodas.parity.test.ts las compara).
 *
 * auditoría 23-09 clientes#1: la lista vacía o sin la clave ya NO significa «todas». Quitar del
 * piloto las dos cuentas QA —la forma natural de «apagarlo»— lo habría encendido para todas las
 * empresas a la vez (interfaz, triggers y RPC). Encender para una empresa es ponerla en la lista.
 *
 * «Todas» es EXPLÍCITO (petición de Pablo, 05-10-2026): `{"enabled": true, "all": true}`, que
 * vale también para las empresas que se den de alta después. Solo el booleano `true` cuenta, en
 * `enabled` y en `all`: un texto «true» o un 1 es apagado. Ojo: la base tiene que llevar la
 * MISMA regla antes de poner `all` en producción (docs/radar/interruptores.md).
 */
export function fociEnabledFor(setting: FociSetting | null | undefined, companyId: string | null | undefined): boolean {
  if (!setting || typeof setting !== "object" || !companyId) return false;
  const s = setting as { enabled?: unknown; all?: unknown; companies?: unknown };
  if (s.enabled !== true) return false;
  if (s.all === true) return true;
  return Array.isArray(s.companies) && s.companies.includes(companyId);
}

/**
 * ¿Están encendidos los focos de plaga para ESTA empresa?
 *
 * Un solo interruptor para la base de datos y la interfaz: la fila `pest_foci`
 * de `platform_settings`, que es la misma que consultan las RPC y los triggers.
 * Apagado, la app es exactamente la de antes: una chincheta por parte, sin
 * pregunta al trabajador, y se confirma por el camino de siempre. Encender o
 * apagar no pierde nada — los focos siguen en su tabla.
 *
 * Mientras carga vale `false`: ante la duda, el camino viejo, que funciona sin focos.
 */
export function useFociEnabled() {
  const { company } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["pest-foci-enabled"],
    queryFn: async (): Promise<FociSetting | null> => {
      const { data } = await supabase.from("platform_settings").select("value").eq("key", "pest_foci").maybeSingle();
      return ((data as { value?: FociSetting } | null)?.value ?? null) as FociSetting | null;
    },
    staleTime: 5 * 60_000,
  });
  return { enabled: fociEnabledFor(data, company?.id), loading: isLoading };
}
